import { apiClient, ApiError } from "@/shared/apiClient";
import type {
  Shift,
  StartShiftRequest,
  EndShiftRequest,
  UpdateShiftNotesRequest,
  ChecklistShiftData,
  ScheduledShift,
} from "./types";
import {
  parseEmployeeShiftApiSettings,
  type EmployeeShiftApiSettings,
} from "./employeeShiftApiSettings";

function extractShiftFromBody(body: unknown): Shift {
  if (body && typeof body === "object" && "data" in body) {
    const d = (body as { data: unknown }).data;
    if (d && typeof d === "object") {
      return d as Shift;
    }
  }
  return body as Shift;
}

export interface OpenShiftResult {
  shift: Shift | null;
  settings?: EmployeeShiftApiSettings;
}

export interface ChecklistResult {
  checklist: string[];
  shift: ChecklistShiftData;
  settings?: EmployeeShiftApiSettings;
}

export interface ShiftMutationResult {
  shift: Shift;
  settings?: EmployeeShiftApiSettings;
}

export const shiftsApi = {
  async startShift(
    notes?: string,
    manualArrivalTime?: string | null,
    coords?: { latitude: number; longitude: number } | null
  ): Promise<ShiftMutationResult> {
    try {
      const payload: StartShiftRequest = {};
      if (notes) {
        payload.notes = notes;
      }
      if (manualArrivalTime != null) {
        payload.manual_arrival_time = manualArrivalTime;
      }
      if (coords != null) {
        payload.latitude = coords.latitude;
        payload.longitude = coords.longitude;
      }
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) {
        payload.timezone = tz;
      }
      const response = await apiClient.post("/api/employee/shifts/start", payload);
      const settings = parseEmployeeShiftApiSettings(response);
      const shift = extractShiftFromBody(response);
      return { shift, settings };
    } catch (error) {
      console.error("Error in startShift:", error);
      throw error;
    }
  },

  async endShift(
    notes?: string,
    manualEndTime?: string | null,
    endOfShiftChecklist?: Array<{ item: string; completed: boolean }>,
    coords?: { latitude: number; longitude: number } | null
  ): Promise<ShiftMutationResult> {
    try {
      const payload: EndShiftRequest = {};
      if (notes) {
        payload.notes = notes;
      }
      if (manualEndTime != null) {
        payload.manual_end_time = manualEndTime;
      }
      if (endOfShiftChecklist) {
        payload.end_of_shift_checklist = endOfShiftChecklist;
      }
      if (coords != null) {
        payload.latitude = coords.latitude;
        payload.longitude = coords.longitude;
      }
      const response = await apiClient.post("/api/employee/shifts/end", payload);
      const settings = parseEmployeeShiftApiSettings(response);
      const shift = extractShiftFromBody(response);
      return { shift, settings };
    } catch (error) {
      console.error("Error in endShift:", error);
      throw error;
    }
  },

  async getChecklist(): Promise<ChecklistResult> {
    try {
      const response = await apiClient.get("/api/employee/checklist");
      const settings = parseEmployeeShiftApiSettings(response);
      // Handle both response formats: { success: true, data: { checklist: [...], shift: {...} } } or just { checklist: [...], shift: {...} }
      if (response.data) {
        return {
          checklist: response.data.checklist || [],
          shift: response.data.shift || { started_at: "" },
          settings,
        };
      }
      return {
        checklist: response.checklist || [],
        shift: response.shift || { started_at: "" },
        settings,
      };
    } catch (error) {
      console.error("Error in getChecklist:", error);
      // If there's an error, return empty data
      return {
        checklist: [],
        shift: { started_at: "" },
      };
    }
  },

  async updateShiftNotes(
    shiftId: number,
    notes: string,
    endOfShiftChecklist?: Array<{ item: string; completed: boolean }>
  ): Promise<Shift> {
    try {
      const payload: UpdateShiftNotesRequest = { notes };
      if (endOfShiftChecklist) {
        payload.end_of_shift_checklist = endOfShiftChecklist;
      }
      const response = await apiClient.patch(
        `/api/employee/shifts/${shiftId}/notes`,
        payload
      );
      // Handle both response formats: { success: true, data: Shift } or just Shift
      return response.data || response;
    } catch (error) {
      console.error("Error in updateShiftNotes:", error);
      throw error;
    }
  },

  async getOpenShift(): Promise<OpenShiftResult> {
    try {
      const response = await apiClient.get("/api/employee/shifts/open");
      const settings = parseEmployeeShiftApiSettings(response);

      if (response === null || response === undefined) {
        return { shift: null, settings };
      }

      if (typeof response === "object" && "data" in response) {
        return {
          shift: (response as { data: Shift | null }).data ?? null,
          settings,
        };
      }

      if (
        typeof response === "object" &&
        "id" in response &&
        "started_at" in response
      ) {
        return { shift: response as Shift, settings };
      }

      return { shift: null, settings };
    } catch (error) {
      console.error("Error in getOpenShift:", error);
      // If there's no open shift, the API might return 404, which is fine
      if (error instanceof ApiError && error.status === 404) {
        return { shift: null };
      }
      throw error;
    }
  },

  async getShifts(): Promise<Shift[]> {
    try {
      const response = await apiClient.get("/api/employee/shifts");
      console.log("shifts response", response);
      // Handle both response formats: { success: true, data: [...] } or just [...]
      if (Array.isArray(response)) {
        return response;
      }
      return response.data || [];
    } catch (error) {
      console.error("Error in getShifts:", error);
      throw error;
    }
  },

  /**
   * Fetch actual (worked) shifts whose `started_at` falls within
   * [startDate, endDate). Both are ISO datetime strings.
   * Falls back to returning all shifts if the backend ignores the params.
   */
  async getShiftsByRange(startDate: string, endDate: string): Promise<Shift[]> {
    try {
      const qs = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      }).toString();
      const response = await apiClient.get(`/api/employee/shifts?${qs}`);
      if (Array.isArray(response)) {
        return response;
      }
      return response?.data || [];
    } catch (error) {
      console.error("Error in getShiftsByRange:", error);
      throw error;
    }
  },

  /**
   * Fetch scheduled shifts that overlap the [startDate, endDate) window.
   * `startDate` is inclusive, `endDate` is exclusive. Both should be ISO
   * date or datetime strings.
   */
  async getScheduledShifts(
    startDate: string,
    endDate: string
  ): Promise<ScheduledShift[]> {
    try {
      const qs = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      }).toString();
      const response = await apiClient.get(
        `/api/employee/shifts/scheduled?${qs}`
      );
      // Accept both { success, data: [...] } and a bare array.
      if (Array.isArray(response)) {
        return response;
      }
      return response?.data || [];
    } catch (error) {
      console.error("Error in getScheduledShifts:", error);
      throw error;
    }
  },
};

